export default class Search extends HTMLElement {
  elements = {
    form: this.querySelector('form'),
    input: this.querySelector('input'),
    button: this.querySelector('button'),
    results: this.querySelector('[data-predictive-search]'),
  };

  listenersActive = false;

  searchTerm = '';

  debounceObj = null;

  boundDocumentKeyboardController = this.documentKeyboardController.bind(this);
  boundHandleClickOutside = this.handleClickOutside.bind(this);
  boundHandleExternalOpen = this.open.bind(this);
  boundHandleExternalClose = this.close.bind(this);

  CACHE_KEY = 'predictive-search-cache';

  constructor() {
    super();

    Object.values(this.elements).forEach(element => {
      if (!element) {
        throw new Error('Search component must have all form, input, button, and results elements.');
      }
    });
  }

  connectedCallback() {
    document.addEventListener('keydown', this.boundDocumentKeyboardController);
    document.addEventListener('search:open', this.boundHandleExternalOpen);
    document.addEventListener('search:close', this.boundHandleExternalClose);

    this.elements.form?.addEventListener('submit', this.handleSubmit.bind(this));

    this.elements.input?.addEventListener('keydown', this.handleInputKeydown.bind(this));

    this.elements.input?.addEventListener('input', this.handleInputInput.bind(this));

    this.elements.input?.addEventListener('focus', this.handleInputFocus.bind(this));
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.boundDocumentKeyboardController);
    document.removeEventListener('search:open', this.boundHandleExternalOpen);
    document.removeEventListener('search:close', this.boundHandleExternalClose);
  }

  documentKeyboardController(e) {
    if (e.ctrlKey && e.key === 'k') {
      this.open();
    }
    if (e.key === 'Escape') {
      this.close();
    }
  }

  handleSubmit(e) {
    if (!this.elements.form) {
      throw new Error('Search component must have a form element.');
    }

    e.preventDefault();
    const formData = new FormData(this.elements.form);
    const searchTerm = formData.get('q');
    this.searchTerm = searchTerm;
    this.navigateToSeachPage();
  }

  handleInputKeydown(e) {
    if (e.key === 'Enter') {
      this.navigateToSeachPage();
    }
  }

  handleInputFocus() {
    if (this.searchTerm.length > 2) {
      this.handleInputInput();
    }
  }

  handleInputInput() {
    this.searchTerm = this.elements.input?.value;

    if (this.searchTerm.length > 2) {
      this.debouncedPredictiveSearch();
    } else {
      this.clearPredictiveSearch();
    }
  }

  navigateToSeachPage() {
    if (this.elements.form) {
      const pathname = this.elements.form.action;
      const url = new URL(pathname);
      url.searchParams.set('q', this.searchTerm);
      window.location.href = url.toString();
    } else {
      throw new Error('Search component must have a form element.');
    }
  }

  open() {
    if (this.hasAttribute('data-open')) {
      return;
    }

    this.toggleCloseListeners(true);

    this.setAttribute('data-open', '');
    this.elements.input?.focus();

    this.dispatchEvent(new CustomEvent('search:opened', { bubbles: true }));
  }

  close() {
    if (!this.hasAttribute('data-open')) {
      return;
    }

    this.toggleCloseListeners(false);

    this.removeAttribute('data-open');

    this.dispatchEvent(new CustomEvent('search:closed', { bubbles: true }));
  }

  debouncedPredictiveSearch() {
    if (this.debounceObj) {
      clearTimeout(this.debounceObj);
    }
    this.debounceObj = setTimeout(async () => {
      await this.predictiveSearch();
    }, 500);
  }

  async predictiveSearch() {
    try {
      const basePathname = this.elements.form?.action;

      if (!basePathname) {
        throw new Error('Search component must have a form element with an action attribute.');
      }

      const sectionId = this.getAttribute('data-section-id');

      if (!sectionId) {
        throw new Error('Search component must have a section id attribute.');
      }

      const isCached = this.getCachedSearchResults(this.searchTerm);

      if (isCached) {
        this.updateUIFromHtmlString(isCached.html);
        return;
      }

      const url = new URL(`${basePathname}/suggest`);
      url.searchParams.set('q', this.searchTerm);
      url.searchParams.set('section_id', sectionId);
      const response = await fetch(url.toString());
      const data = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(data, 'text/html');
      const results = doc.querySelector(`${this.tagName}[data-section-id="${sectionId}"] [data-predictive-search]`);

      if (results) {
        this.updateUIFromHtmlString(results.innerHTML);
        this.setCachedSearchResults(this.searchTerm, results.innerHTML);
      }
    } catch (error) {
      console.error('Error fetching predictive search:', error);
    }
  }

  clearPredictiveSearch() {
    const { results } = this.elements;
    if (results) {
      results.innerHTML = '';
    }
    this.removeAttribute('data-results-visible');
    this.toggleCloseListeners(false);
  }

  toggleCloseListeners(isOpen) {
    if (isOpen && !this.listenersActive) {
      this.listenersActive = true;
      // prevent the modal to instantly close when clicking the open button
      setTimeout(() => {
        document.addEventListener('click', this.boundHandleClickOutside);
        document.addEventListener('touchstart', this.boundHandleClickOutside);
        document.addEventListener('scroll', this.boundHandleClickOutside);
      }, 0);
    } else if (!isOpen && this.listenersActive) {
      this.listenersActive = false;
      document.removeEventListener('click', this.boundHandleClickOutside);
      document.removeEventListener('touchstart', this.boundHandleClickOutside);
      document.removeEventListener('scroll', this.boundHandleClickOutside);
    }
  }

  handleClickOutside(e) {
    if (!this.contains(e.target) && this.hasAttribute('data-open')) {
      this.close();
    }
    this.elements.input?.blur();
    this.clearPredictiveSearch();
  }

  updateUIFromHtmlString(htmlString) {
    if (this.elements.results) {
      this.elements.results.innerHTML = htmlString;
      this.setAttribute('data-results-visible', '');
      this.toggleCloseListeners(true);
    }
  }

  setCachedSearchResults(term, html) {
    const isCached = JSON.parse(sessionStorage.getItem(this.CACHE_KEY) || '[]');

    if (isCached.length > 3) {
      isCached.shift();
    }

    isCached.push({ term, html });

    sessionStorage.setItem(this.CACHE_KEY, JSON.stringify(isCached));
  }

  getCachedSearchResults(term) {
    const isCached = JSON.parse(sessionStorage.getItem(this.CACHE_KEY) || '[]');

    return isCached.find(item => item.term === term) || null;
  }
}
